'use client'
import { Grid as AscendGrid, Link as AscendLink } from '@achieve/ascend'
import { MenuList, MenuItem } from 'components/Menu'
import { useState, useEffect, useRef } from 'react'
import { useViewportSmallerThan } from 'utils/viewport'
import { BREAKPOINTS } from 'constants/viewport'
import Section from 'components/Section'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { StickySubNavSection, StickySubNavSectionScroll } from './StickySubNavSection'
import { normalizeContent } from 'utils/shared/hacks'
import classNames from 'classnames'
import { SITE_ENVIRONMENTS } from 'constants/websites'

function StickySubNavWrapper({
  content,
  scrollRender = false,
  resolvedSectionContents,
  splitContent = false,
}) {
  const isMobile = useViewportSmallerThan(BREAKPOINTS.lg)
  const [sectionContents, setSectionContents] = useState(
    resolvedSectionContents || content?.fields?.sectionContents
  )
  const isFdr = process.env.NEXT_PUBLIC_SITE_ENVIRONMENT === SITE_ENVIRONMENTS.FDR

  // Declare array in state for tracking position on page to highlight subnav bar
  const [intersectingElements, setIntersectingElements] = useState(
    new Array(sectionContents.length).fill(true)
  )

  // Create a ref for the subnav bar for better tabbing behavior
  const refContainer = useRef()

  // Create a ref for each major section within the policies main text area.
  const refElements = useRef()
  refElements.current = []

  // IntersectionObserver to track sections are intersecting viewport by more than {threshold}%
  useEffect(() => {
    const observer = new IntersectionObserver(handleIntersectionEvent, {
      rootMargin: '-150px', //viewport margin to be excluded when calculating section intersection
      threshold: 0.01, //1% of section visible in viewport is threshold to be intersecting
    })

    refElements.current.forEach((ref) => {
      if (!(ref instanceof Element)) return
      observer.observe(ref)
    })
    return () => {
      refElements.current.forEach((ref) => {
        if (!(ref instanceof Element)) return
        if (ref) observer.unobserve(ref)
      })
    }
  }, [sectionContents])

  function handleIntersectionEvent(entries) {
    const sectionObservedIndex = entries[0]?.target.getAttribute('data-section-index')
    const sectionIsIntersecting = entries[0].isIntersecting

    setIntersectingElements((prev) => {
      if (sectionObservedIndex == 0 || prev[sectionObservedIndex - 1] === false) {
        prev[sectionObservedIndex] = sectionIsIntersecting
      }
      return [...prev]
    })
  }

  // Update state on link click
  const updateIntersectingElementsOnClick = (e) => {
    let sectionClicked = 0
    refContainer.current.focus()

    if (e?.target?.getAttribute('section-index')) {
      sectionClicked = e?.target?.getAttribute('section-index')
    }

    setIntersectingElements((prev) => {
      const newState = new Array(prev.length).fill(false).fill(true, sectionClicked)
      return isMobile ? prev : newState
    })
  }

  // Dynamically add ref to section if section ref does not already exist
  const addToRefs = (observedSection, index) => {
    refElements.current[index] = observedSection
  }

  const { dynamicPathname, hash } = useAppRouter()
  const /** @type string */ pageTestIdScope = dynamicPathname.split('/').pop()

  /**
   * @param {string} testId
   * @param {string} scope
   * @returns {string}
   */
  const makeTestId = (testId, scope) => {
    if (scope) return `${scope}-${testId}`
    return testId
  }

  const subNavMenuItemCreator = (
    sectionContents,
    intersectingElements,
    updateIntersectingElementsOnClick
  ) => {
    // Find the first section(closest to top of page) that is intersecting the screen area
    const minIntersectingElementIndex = intersectingElements.findIndex((el) => el)
    return (sectionContents || []).map((content, index) => {
      const sectionContentId = content?.fields?.name?.replaceAll(' ', '-')
      const sectionContentName = content?.fields?.name

      return (
        <MenuItem
          key={content?.fields?.sys?.id || `sticky-subnav-${index}`}
          className={classNames(
            'in-[.vertical-menu]:whitespace-normal in-[.vertical-menu]:min-h-auto in-[.vertical-menu]:bg-white p-0',
            '[&_a]:in-[.vertical-menu]:border-l-neutral-75 [&_a]:in-[.vertical-menu]:text-[#1d252f] [&_a]:in-[.vertical-menu]:no-underline [&_a]:in-[.vertical-menu]:w-full [&_a]:in-[.vertical-menu]:px-6 [&_a]:in-[.vertical-menu]:py-4 [&_a]:in-[.vertical-menu]:border-l-4 [&_a]:in-[.vertical-menu]:border-solid [&_a]:in-[.vertical-menu]:border-b-0 [&_a]:in-[.vertical-menu]:border-t-0 [&_a]:in-[.vertical-menu]:border-r-0',
            '[&_a[data-active="true"]]:in-[.vertical-menu]:bg-white [&_a[data-active="true"]]:in-[.vertical-menu]:z-1 [&_a[data-active="true"]]:in-[.vertical-menu]:text-blue-500 [&_a[data-active="true"]]:in-[.vertical-menu]:border-blue-500',
            'lg:[&_a]:hover:bg-white lg:[&_a]:hover:z-1 lg:[&_a]:hover:text-blue-500 lg:[&_a]:hover:border-blue-500',
            'lg:[&_a]:active:bg-white lg:[&_a]:active:z-1 lg:[&_a]:active:text-blue-500 lg:[&_a]:active:border-blue-500',
            'lg:[&_a]:focus-within:bg-white lg:[&_a]:focus-within:z-1 lg:[&_a]:focus-within:text-blue-500 lg:[&_a]:focus-within:border-blue-500',
            'lg:[&_a]:focus:bg-white lg:[&_a]:focus:z-1 lg:[&_a]:focus:text-blue-500 lg:[&_a]:focus:border-blue-500'
          )}
          tabIndex={-1}
          onClick={updateIntersectingElementsOnClick}
          data-testid={makeTestId(`navbar-item-${index}`, pageTestIdScope)}
        >
          <AscendLink
            section-index={index}
            className="bodyLg lg:headingMd"
            href={`#${sectionContentId}`}
            //if mobile view, always highlight first navbar option, else top intersecting section
            data-active={isMobile ? index === 0 : index === minIntersectingElementIndex}
            aria-current={index === minIntersectingElementIndex}
            data-testid={makeTestId(
              `vertical-nav-${sectionContentName}-${content?.sys?.id}`,
              pageTestIdScope
            )}
          >
            {sectionContentName}
          </AscendLink>
        </MenuItem>
      )
    })
  }

  const renderDefaultContent = (sectionContent, index) => {
    const ComponentSection =
      resolvedSectionContents || !scrollRender ? StickySubNavSection : StickySubNavSectionScroll
    const handleSetSectionContents = (data) => {
      const currentData = normalizeContent(sectionContents)
      currentData[index] = data
      setSectionContents(currentData)
    }
    return (
      <ComponentSection
        key={sectionContent?.sys?.id || `sticky-section-${index}`}
        sectionContent={sectionContent}
        index={index}
        addToRefs={addToRefs}
        makeTestId={makeTestId}
        pageTestIdScope={pageTestIdScope}
        scrollRender={scrollRender}
        setSectionContents={handleSetSectionContents}
      />
    )
  }

  useEffect(() => {
    if (!hash) return

    const scrollToHash = () => {
      const el = document.getElementById(hash)
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' })
      }
    }

    // Try to scroll immediately in case the element is already rendered
    scrollToHash()

    // In case the sections render asynchronously (e.g., lazy loaded content),
    // attempt to scroll again shortly after the first try.
    const timeoutId = setTimeout(scrollToHash, 500)

    return () => clearTimeout(timeoutId)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hash, sectionContents])

  const contentItems = (sectionContents || []).map(renderDefaultContent)

  // splitContent (opt-in via the section's jsonLd) stacks each section title above its
  // content instead of rendering the sticky subnav beside it. Checked before the FDR
  // layout so the flag works on every brand; FDR keeps its centered column.
  if (splitContent) {
    return (
      <Section
        className={'m-0 pt-0 pb-8 px-0 lg:m-0 lg:pb-16'}
        data-testid={makeTestId('body-section', pageTestIdScope)}
      >
        <AscendGrid
          container
          data-testid={makeTestId('body-outer-grid-container', pageTestIdScope)}
          justifyContent={isFdr ? 'center' : undefined}
        >
          <AscendGrid item xs={12} md={isFdr ? 10 : 12} lg={isFdr ? 8 : 12}>
            {(sectionContents || []).map((sectionContent, index) => {
              const Heading = index === 0 ? 'h1' : 'h2'
              return (
                <div key={sectionContent?.sys?.id || `split-section-${index}`}>
                  <Heading
                    className="bodyLg lg:headingMd text-blue-500 border-l-4 border-solid border-y-0 border-r-0 border-blue-500 px-6 py-4 mt-0 mb-8"
                    data-testid={makeTestId(`split-section-heading-${index}`, pageTestIdScope)}
                  >
                    {sectionContent?.fields?.name}
                  </Heading>
                  {renderDefaultContent(sectionContent, index)}
                </div>
              )
            })}
          </AscendGrid>
        </AscendGrid>
      </Section>
    )
  }

  if (isFdr) {
    return (
      <Section
        className={'m-0 pt-0 pb-8 px-0 lg:m-0 lg:pb-16'}
        data-testid={makeTestId('body-section', pageTestIdScope)}
      >
        <AscendGrid
          container
          data-testid={makeTestId('body-outer-grid-container', pageTestIdScope)}
          justifyContent="center"
        >
          <AscendGrid item xs={12} md={10} lg={8}>
            {contentItems}
          </AscendGrid>
        </AscendGrid>
      </Section>
    )
  }

  return (
    <Section
      className={'m-0 pt-0 pb-8 px-0 lg:m-0 lg:pb-16'}
      data-testid={makeTestId('body-section', pageTestIdScope)}
    >
      <AscendGrid container data-testid={makeTestId('body-outer-grid-container', pageTestIdScope)}>
        <AscendGrid
          item
          xs={12}
          lg={4}
          data-testid={makeTestId('body-outer-grid-container', pageTestIdScope)}
        >
          <MenuList
            className={'vertical-menu sticky mb-8 p-0 top-24 lg:pr-4'}
            ref={refContainer}
            tabIndex={0}
            data-testid={makeTestId('vertical-nav-menu-container', pageTestIdScope)}
          >
            {subNavMenuItemCreator(
              sectionContents,
              intersectingElements,
              updateIntersectingElementsOnClick
            )}
          </MenuList>
        </AscendGrid>
        <AscendGrid item xs={12} lg={8}>
          {contentItems}
        </AscendGrid>
      </AscendGrid>
    </Section>
  )
}
export { StickySubNavWrapper }
export default StickySubNavWrapper
